import { lazy, Suspense, useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LoadingSplash } from "@/components/LoadingSplash";
import { AnimatePresence, motion } from "framer-motion";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";

// ─── Bug 1 Fix: Lazy-load all non-landing routes ─────────────────
const Auth = lazy(() => import("./pages/Auth.tsx"));
const AuthCallback = lazy(() => import("./pages/AuthCallback.tsx"));
const Dashboard = lazy(() => import("./pages/Dashboard.tsx"));
const DashboardThreats = lazy(() => import("./pages/DashboardThreats.tsx"));
const DashboardAnalytics = lazy(() => import("./pages/DashboardAnalytics.tsx"));
const DashboardSettings = lazy(() => import("./pages/DashboardSettings.tsx"));
const DashboardProfile = lazy(() => import("./pages/DashboardProfile.tsx"));
const DashboardDomains = lazy(() => import("./pages/DashboardDomains.tsx"));
const DashboardAdmin = lazy(() => import("./pages/DashboardAdmin.tsx"));
const DashboardAdminUsers = lazy(() => import("./pages/DashboardAdminUsers.tsx"));
const DomainSetup = lazy(() => import("./pages/DomainSetup.tsx"));
const DashboardAITraffic = lazy(() => import("./pages/DashboardAITraffic.tsx"));
const AdminRoute = lazy(() => import("./components/AdminRoute.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const BlogWhatIsBotDetection = lazy(() => import("./pages/BlogWhatIsBotDetection.tsx"));
const BlogStopBotsScraping = lazy(() => import("./pages/BlogStopBotsScraping.tsx"));
const BlogBotDetectionScript = lazy(() => import("./pages/BlogBotDetectionScript.tsx"));
const AlternativesCloudflare = lazy(() => import("./pages/AlternativesCloudflare.tsx"));
const Security = lazy(() => import("./pages/Security.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const DashboardLayout = lazy(() => import("./components/dashboard/DashboardLayout.tsx").then(m => ({ default: m.DashboardLayout })));

// ─── Bug 2 Fix: Resilient QueryClient config ─────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      gcTime: 5 * 60 * 1000,
    },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LazyFallback() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dashboard");

  return (
    <Suspense fallback={<LazyFallback />}>
      {isDashboard ? (
        <Routes location={location}>
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="threats" element={<DashboardThreats />} />
            <Route path="analytics" element={<DashboardAnalytics />} />
            <Route path="ai-traffic" element={<DashboardAITraffic />} />
            <Route path="domains" element={<DashboardDomains />} />
            <Route path="domains/:id/setup" element={<DomainSetup />} />
            <Route path="settings" element={<DashboardSettings />} />
            <Route path="profile" element={<DashboardProfile />} />
            <Route path="admin" element={<Suspense fallback={<LazyFallback />}><AdminRoute><DashboardAdmin /></AdminRoute></Suspense>} />
            <Route path="admin/users" element={<Suspense fallback={<LazyFallback />}><AdminRoute><DashboardAdminUsers /></AdminRoute></Suspense>} />
            <Route path="*" element={<div className="text-center py-20"><h2 className="font-display text-xl font-bold text-foreground mb-2">Page not found</h2><p className="text-sm font-mono text-muted-foreground">This dashboard page doesn't exist.</p></div>} />
          </Route>
        </Routes>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Routes location={location}>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/confirm" element={<AuthCallback />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/blog/what-is-bot-detection" element={<BlogWhatIsBotDetection />} />
              <Route path="/blog/stop-bots-scraping-website" element={<BlogStopBotsScraping />} />
              <Route path="/blog/bot-detection-script" element={<BlogBotDetectionScript />} />
              <Route path="/alternatives/cloudflare-bot-management" element={<AlternativesCloudflare />} />
              <Route path="/security" element={<Security />} />
              <Route path="/about" element={<About />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      )}
    </Suspense>
  );
}

const PUBLIC_PREFIXES = ["/", "/blog", "/about", "/security", "/privacy", "/terms", "/alternatives"];

function isPublicPath(path: string) {
  if (path === "/") return true;
  return PUBLIC_PREFIXES.some((p) => p !== "/" && path.startsWith(p));
}

function AppShell() {
  const { loading } = useAuth();
  const [splashDone, setSplashDone] = useState(false);
  const isPublic = isPublicPath(window.location.pathname);

  useEffect(() => {
    if (isPublic || !loading) {
      const timer = setTimeout(() => setSplashDone(true), isPublic ? 0 : 800);
      return () => clearTimeout(timer);
    }
  }, [loading, isPublic]);

  const showSplash = isPublic ? false : loading || !splashDone;

  return (
    <>
      <LoadingSplash show={showSplash} />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AnimatedRoutes />
      </BrowserRouter>
    </>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <AppShell />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
